import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import {
  HelpCircle, Send, X, Loader2, BookOpen, ChevronRight,
  CheckCircle2, ExternalLink
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HelpChatWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I can help you navigate HumanScaled. Ask a question or click 'Guide Me' on any help article.", citations: [] },
  ]);
  const [guidedSteps, setGuidedSteps] = useState(null);
  const [guidedArticle, setGuidedArticle] = useState(null);
  const [checkedSteps, setCheckedSteps] = useState({});
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, guidedSteps]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const q = input.trim();
    setMessages(prev => [...prev, { role: 'user', content: q }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/help/ask`, {
        question: q,
        route: location.pathname,
        guided_article_slug: guidedArticle?.slug || null,
      });
      const data = res.data;
      setMessages(prev => [...prev, { role: 'assistant', content: data.answer, citations: data.citations || [] }]);
      if (data.guided_steps) {
        setGuidedSteps(data.guided_steps);
        setGuidedArticle(data.guided_article);
        setCheckedSteps({});
        setCurrentStepIndex(0);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'I had trouble with that. Try browsing the Help Center at /help.', citations: [] }]);
    } finally { setLoading(false); }
  };

  const handleNextStep = () => {
    if (!guidedSteps) return;
    setCheckedSteps(prev => ({ ...prev, [currentStepIndex]: true }));
    if (currentStepIndex < guidedSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      setMessages(prev => [...prev, { role: 'assistant', content: 'All steps completed! You can now ask another question or explore more SOPs.', citations: [] }]);
    }
  };

  const startGuide = async (slug) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/help/ask`, { question: 'Walk me through this', route: location.pathname, guided_article_slug: slug });
      const data = res.data;
      setMessages(prev => [...prev, { role: 'assistant', content: data.answer, citations: data.citations || [] }]);
      if (data.guided_steps) {
        setGuidedSteps(data.guided_steps);
        setGuidedArticle(data.guided_article);
        setCheckedSteps({});
        setCurrentStepIndex(0);
      }
    } catch { toast.error('Failed to start guide'); }
    finally { setLoading(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="fixed bottom-16 right-6 z-40 w-11 h-11 bg-violet-600 rounded-full flex items-center justify-center shadow-lg shadow-violet-600/20 hover:bg-violet-500 transition-colors" data-testid="help-chat-bubble">
        <HelpCircle className="w-5 h-5 text-white" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-16 right-6 z-50 w-80 sm:w-96 bg-[#0a0a0a] border border-white/10 shadow-2xl flex flex-col" style={{ height: 480 }} data-testid="help-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-violet-400" />
          <span className="text-sm font-bold">Help Agent</span>
          <span className="w-1.5 h-1.5 bg-violet-500 rounded-full" />
        </div>
        <div className="flex items-center gap-2">
          <Link to="/help" className="text-[10px] text-muted-foreground hover:text-white">All SOPs</Link>
          <button onClick={() => setOpen(false)} className="p-1 text-muted-foreground hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[88%] px-3 py-2 text-sm leading-relaxed ${msg.role === 'user' ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-white/5 border border-white/10'}`}>
              {msg.content}
              {/* Citations */}
              {msg.citations?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/5 space-y-1">
                  {msg.citations.slice(0, 3).map(c => (
                    <Link key={c.slug} to={c.link} onClick={() => setOpen(false)} className="flex items-center gap-1.5 text-[10px] text-violet-400 hover:text-violet-300">
                      <ExternalLink className="w-3 h-3" />
                      {c.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start"><div className="px-3 py-2 bg-white/5 border border-white/10"><Loader2 className="w-4 h-4 animate-spin text-violet-400" /></div></div>
        )}
        <div ref={scrollRef} />
      </div>

      {/* Guided Steps Panel */}
      {guidedSteps && (
        <div className="border-t border-white/5 px-3 py-2 bg-violet-500/5 max-h-36 overflow-y-auto" data-testid="guided-panel">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-violet-400 font-mono uppercase">
              {guidedArticle?.title?.slice(0, 30)}
            </span>
            <button onClick={() => { setGuidedSteps(null); setGuidedArticle(null); }} className="text-[10px] text-muted-foreground hover:text-white">Exit Guide</button>
          </div>
          <div className="space-y-1">
            {guidedSteps.map((step, i) => (
              <div key={i} className={`flex items-start gap-2 py-1 text-[11px] ${i === currentStepIndex ? 'text-white' : checkedSteps[i] ? 'text-emerald-400/60' : 'text-muted-foreground'}`}>
                {checkedSteps[i] ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /> : <span className="w-3.5 text-center flex-shrink-0 font-mono">{step.step}</span>}
                <span className={checkedSteps[i] ? 'line-through' : i === currentStepIndex ? 'font-medium' : ''}>{step.action}</span>
              </div>
            ))}
          </div>
          {currentStepIndex < guidedSteps.length && (
            <Button onClick={handleNextStep} className="w-full mt-2 bg-violet-600 text-white hover:bg-violet-500 rounded-sm h-7 text-[10px]" data-testid="next-step-btn">
              <ChevronRight className="w-3 h-3 mr-0.5" /> {checkedSteps[currentStepIndex] ? 'Next Step' : `Complete Step ${guidedSteps[currentStepIndex]?.step}`}
            </Button>
          )}
          {guidedArticle && (
            <Link to={guidedArticle.link} onClick={() => setOpen(false)} className="block text-center text-[10px] text-violet-400 hover:text-violet-300 mt-1.5">
              Open Full SOP
            </Link>
          )}
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t border-white/5 flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} placeholder="Ask about HumanScaled..." className="flex-1 bg-black/30 border border-white/10 rounded-sm px-3 py-2 text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:border-violet-500/50" data-testid="help-chat-input" />
        <button onClick={handleSend} disabled={loading || !input.trim()} className="bg-violet-600 text-white px-3 rounded-sm hover:bg-violet-500 disabled:opacity-50" data-testid="help-chat-send"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export default HelpChatWidget;
